// Route-segment parsers.
//
// The SPA used to base64-encode every id/email before stuffing it into the
// URL path (``/read/<btoa(id)>``, ``/profile/<btoa(email)>`` etc.). That was
// costless obfuscation, not security — it also broke deep-links from
// subtrees that skipped the encode (Search links a plain ``book.id`` at
// ``/read/``) and threw on hand-edited URLs.
//
// These helpers accept the plain form going forward **and** silently
// decode the legacy base64 form so bookmarks from the old scheme still
// resolve. When both interpretations are possible the plain form wins.

import { safeAtob } from './safeHtml';

function looksLikeNumericId(value) {
  return typeof value === 'string' && /^[1-9]\d*$/.test(value);
}

function looksLikeEmail(value) {
  return typeof value === 'string' && value.includes('@') && !value.includes(' ');
}

/** Parse ``/read/:segment`` → numeric project id string, or '' when unusable. */
export function projectIdFromSegment(segment) {
  if (!segment) return '';
  const raw = decodeURIComponent(segment);
  if (looksLikeNumericId(raw)) return raw;
  const decoded = safeAtob(raw);
  if (decoded && looksLikeNumericId(decoded)) return decoded;
  return '';
}

/**
 * Parse ``/editor/:segment`` — returns ``{ chapterId, docname }``. Exactly one
 * of the two is set; ``chapterId`` takes priority. Both empty means the
 * segment is garbage.
 */
export function chapterRefFromSegment(segment) {
  if (!segment) return { chapterId: '', docname: '' };
  const raw = decodeURIComponent(segment);
  if (looksLikeNumericId(raw)) return { chapterId: raw, docname: '' };
  const decoded = safeAtob(raw);
  if (decoded && looksLikeNumericId(decoded)) {
    return { chapterId: decoded, docname: '' };
  }
  // Legacy base64-of-docname scheme.
  if (decoded) return { chapterId: '', docname: decoded };
  // Last resort: the segment itself is a docname that slipped through raw.
  return { chapterId: '', docname: raw };
}

/** Parse ``/profile/:segment`` → email string, or '' when unusable. */
export function emailFromSegment(segment) {
  if (!segment) return '';
  const raw = decodeURIComponent(segment);
  if (looksLikeEmail(raw)) return raw;
  const decoded = safeAtob(raw);
  if (decoded && looksLikeEmail(decoded)) return decoded;
  return '';
}

/**
 * Discriminated parse of ``/profile/:segment``. Returns
 * ``{ kind: 'email' | 'username' | 'empty', value }``. The Profile
 * page uses ``kind`` to pick the right backend lookup
 * (``findByEmail`` vs ``byUsername``). Backwards compat: legacy
 * base64-of-email URLs still resolve to ``kind: 'email'``.
 */
export function profileSegmentParse(segment) {
  if (!segment) return { kind: 'empty', value: '' };
  const raw = decodeURIComponent(segment);
  if (looksLikeEmail(raw)) return { kind: 'email', value: raw };
  const decoded = safeAtob(raw);
  if (decoded && looksLikeEmail(decoded)) {
    return { kind: 'email', value: decoded };
  }
  // Anything else is treated as a username — the backend handles
  // case-insensitive matching and returns 404 if it's bogus.
  return { kind: 'username', value: raw };
}

/**
 * Build the ``/profile/<segment>`` URL for a user object. Prefers
 * the ``username`` (URL-friendly handle, no email leak in the bar)
 * and falls back to the email when the username isn't known.
 * Always pass a ``user`` object so this picks the freshest handle
 * available — the link target updates if the user renames.
 */
export function profileSegment(user) {
  if (!user) return '';
  if (typeof user === 'string') return encodeURIComponent(user);
  const handle = user.username || user.handle || '';
  if (handle) return encodeURIComponent(handle);
  if (user.email) return encodeURIComponent(user.email);
  return '';
}

/** Convenience: full ``/profile/<segment>`` path for a user object. */
export function profilePath(user) {
  const seg = profileSegment(user);
  return seg ? `/profile/${seg}` : '/profile';
}
